import React from 'react';

import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { PressControlComponent } from 'design/pressControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { useAccountEmail } from 'hooks/useAccountEmail';
import { useServices } from 'hooks/useServices';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { ContentLayoutComponent } from 'design/contentLayout';
import { MarginComponent } from 'design/margin';
import { useAppState } from 'hooks/useAppState';
import { AuthFormFooterLayoutComponent } from 'design/authFormFooterLayout/authFormFooterLayoutComponents';
import { useIsMobile } from 'hooks/useIsMobile';

type Props = {
  onSubmitSucceed(): void;
};

export const ProdStackManagerEmailConfirm = (props: Props) => {
  const { onSubmitSucceed } = props;

  const { authType, user } = useAppState();

  const authenticatedWithGoogle = user?.accountInfo.authProvider === 'GOOGLE';

  React.useLayoutEffect(() => {
    if (authType === 'MANUAL' || authenticatedWithGoogle) {
      onSubmitSucceed();
    }
  }, [authType, authenticatedWithGoogle, onSubmitSucceed]);

  const email = useAccountEmail();

  const dispatch = useAppDispatcher();

  const isMobile = useIsMobile();

  const { signOut: signOutService } = useServices();

  const { active, injectPromise } = useAsyncBoundary();

  const {
    prodStackManager: { emailConfirm: ownDictionary },
  } = useIntlDictionary();

  const signOut = React.useCallback(() => {
    injectPromise(
      signOutService().then(() => {
        dispatch({ name: 'ProdStackManager__SignedOut' });
      })
    );
  }, [dispatch, injectPromise, signOutService]);

  return (
    <ContentLayoutComponent
      title={
        <TextComponent size="l" family="futura">
          {ownDictionary.titleText}
        </TextComponent>
      }
      body={
        <>
          {email && (
            <TextComponent>{ownDictionary.detailsText(email)}</TextComponent>
          )}
          <MarginComponent top={44} />
          <AuthFormFooterLayoutComponent hasGap axis={isMobile ? 'y' : 'x'}>
            <PressControlComponent
              disabled={active}
              type="button"
              theme="primary"
              hasSpinner={active}
              onPress={signOut}
            >
              {ownDictionary.secondaryActionText}
            </PressControlComponent>
            <PressControlComponent
              disabled={active}
              type="button"
              theme="accent"
              hasSpinner={active}
              onPress={onSubmitSucceed}
            >
              {ownDictionary.primaryActionText}
            </PressControlComponent>
          </AuthFormFooterLayoutComponent>
        </>
      }
      backAction={null}
    />
  );
};
